import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Paperclip, Send, X } from 'lucide-react';
import { Input } from './UI';

const EmailModal = ({ isOpen, onClose, transaction, onSend, generateAttachment }) => {
  const [email, setEmail] = useState(transaction?.customerEmail || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [stage, setStage] = useState(''); // 'pdf' | 'send' | ''

  useEffect(() => {
    if (transaction) {
      setEmail(transaction.customerEmail || '');
      const typeLabels = { invoice: 'Invoice', quote: 'Quotation', po: 'Purchase Order', sj: 'Surat Jalan' };
      setSubject(`${typeLabels[transaction.type] || 'Document'} #${transaction.id}`);
    }
  }, [transaction]);

  const handleSend = async () => {
    if (!email) return;
    setSending(true);
    try {
      let attachmentBase64 = null;
      let attachmentFilename = null;
      if (typeof generateAttachment === 'function') {
        setStage('pdf');
        try {
          const result = await generateAttachment();
          attachmentBase64 = result?.base64 || null;
          attachmentFilename = result?.filename || null;
        } catch (err) {
          console.error('Failed to generate PDF attachment:', err);
          // Continue sending email without attachment rather than blocking the user.
        }
      }
      setStage('send');
      await onSend({ email, subject, message, attachmentBase64, attachmentFilename });
    } finally {
      setSending(false);
      setStage('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const typeLabels = { invoice: 'Invoice', quote: 'Quotation', po: 'Purchase Order', sj: 'Surat Jalan' };
  const docLabel = typeLabels[transaction?.type] || 'Dokumen';
  const attachmentName = transaction ? `${docLabel}_${transaction.id}.pdf` : `${docLabel}.pdf`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-white rounded-2xl shadow-dropdown w-full max-w-lg mx-4 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent/10 rounded-xl">
              <Mail className="text-accent" size={20} />
            </div>
            <h3 className="text-lg font-heading font-bold">Kirim Email</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <Input
            label="Email Penerima"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            required
          />
          <Input
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject email"
          />
          <div className="flex flex-col space-y-2">
            <label className="label">Pesan (Opsional)</label>
            <textarea
              className="input-field resize-none"
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tulis pesan tambahan..."
            />
          </div>

          {typeof generateAttachment === 'function' && (
            <div
              className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3"
              data-testid="email-attachment-info"
            >
              <div className="p-2 bg-primary/10 rounded-lg text-primary flex-shrink-0">
                <Paperclip size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">Lampiran PDF</p>
                <p className="text-xs text-slate-500 truncate">{attachmentName}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={sending}>
            Batal
          </button>
          <button
            onClick={handleSend}
            className="btn-primary flex-1"
            disabled={!email || sending}
            data-testid="send-email-btn"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {stage === 'pdf' ? 'Menyiapkan PDF...' : 'Mengirim...'}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={16} /> Kirim
              </span>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailModal;
